import {
  useTranslation,
  type Catalogues,
  type Locale,
  type TranslationParams,
} from 'escrowbuild-ui';

import { en, type AppPluralKey, type AppTranslationKey } from './en';
import { FIXTURE_PT_BR } from './fixtures.pt-BR';
import { ptBR } from './pt-BR';

/**
 * The two catalogues, in the shape the provider takes.
 *
 * A module-level constant rather than an object literal at the call site: the
 * provider memoises the merge on this reference, and a literal would be a new
 * catalogue on every render of `main.tsx` — one merge of several hundred keys,
 * and a new `t`, per render.
 *
 * Only the application's own strings are passed. The design system merges its
 * `ds.*` base underneath both, so the library's chrome is translated without
 * this file having to know which keys are the library's.
 *
 * The sample dataset's Portuguese rides on the Portuguese catalogue and ONLY on
 * that one. There is deliberately no English half of it: the English for a
 * fixture string is the fixture string, so under `en` these keys miss the
 * catalogue, the lookup returns the key, and `useFixtureT` hands back the record's
 * own English — which is what makes the English output byte-identical to the
 * build that had no fixture translation at all. Putting an English copy here
 * would be a second place for the same sentence to live and drift.
 */
export const CATALOGUES: Catalogues = {
  en,
  'pt-BR': { ...ptBR, ...FIXTURE_PT_BR },
};

/**
 * The application's translator.
 *
 * The design system's `t` takes any string, because the library cannot name a
 * key belonging to a catalogue it has never seen. This narrows it to THIS
 * catalogue's entries — so a misspelt key such as `login.emial` is a compile
 * error rather than a screen that reads `login.emial` — and it does so by
 * declaration rather than by a cast: a function accepting any string already
 * satisfies a signature accepting fewer of them.
 *
 * A key that does not exist still returns itself rather than throwing. A typo
 * that survives to run time should be visible in the interface, not a blank.
 */
export type AppTranslate = (
  key: AppTranslationKey | AppPluralKey,
  params?: TranslationParams,
) => string;

export function useT(): AppTranslate {
  const { t } = useTranslation();
  return t;
}

/**
 * The sample dataset, per collection, in the reader's language.
 *
 * Re-exported here so a screen has one import line for everything it says, and
 * so nothing has to reach into the fixture layer to find them. See `fixtures.ts`
 * for why these are hooks rather than the constants `domain.ts` exports.
 */
export {
  useDocuments,
  useEvidence,
  useExpenses,
  useFixtureVocabulary,
  useMilestones,
  useNotifications,
  useOpportunities,
  usePublications,
  usePublishedDocuments,
  usePublishedEvidence,
  usePublishedPublications,
  useReports,
  useScheduleOf,
  useUpdates,
  type FixtureVocabulary,
} from './fixtures';

export type { AppPluralKey, AppTranslationKey, Locale, TranslationParams };
